import type { BibleBook } from "./types";

/** Job to Song of Solomon — poetry, wisdom and complaint. */
export const WISDOM: BibleBook[] = [
  {
    slug: "job",
    name: "Job",
    testament: "old",
    genre: "wisdom",
    order: 18,
    chapters: 42,
    author: "Anonymous",
    written:
      "Uncertain; the poetry is very old, the final form perhaps sixth century BC",
    audience: "Anyone who has been told their suffering has a reason",
    argument:
      "Thirty-eight chapters of a man demanding an explanation and being given, instead, a tour of the natural world — and the friends who defended God are the ones rebuked at the end.",
    summary: [
      "The frame is short: a righteous man loses everything in two chapters. Then the book slows almost to a stop for thirty-five chapters of speeches, as three friends explain that suffering is punishment and Job must have done something, and Job refuses to agree.",
      "The friends say things that sound orthodox and are wrong. Job says things that sound close to blasphemy and is later told he spoke rightly. That reversal is the single most important thing in the book and the most commonly missed.",
      "When God finally answers, out of a whirlwind, no explanation is given. Instead there are four chapters of questions about wild goats, ostriches, the sea and two enormous animals. Job's response is not that he now understands but that he has stopped needing to.",
    ],
    startHere:
      "Chapter 3, Job's opening lament, and then jump to chapter 38 for the answer. The middle speeches are worth reading eventually and they defeat most first attempts.",
    themes: ["suffering", "argument", "silence", "creation"],
    keyChapters: [
      {
        chapter: 3,
        why: "Job's opening lament, cursing the day he was born, which sets the tone for thirty-five chapters.",
      },
      {
        chapter: 19,
        why: "I know that my Redeemer lives, said in the middle of the bleakest stretch of the book.",
      },
      {
        chapter: 38,
        why: "The answer from the whirlwind, which is four chapters of questions and no explanation.",
      },
    ],
    known: {
      text: "But as for me, I know that my Redeemer lives. In the end, he will stand upon the earth.",
      ref: "Job 19:25",
    },
    misread:
      "That Job is about patience. He is not patient; he is furious, articulate and relentless for thirty-five chapters, and the book's verdict is that this was the right response.",
    faq: [
      {
        q: "What is the message of the book of Job?",
        a: "That suffering is not reliably explained by wrongdoing, and that the demand for an explanation is not itself sinful. The friends who defend God with tidy theology are rebuked; Job, who argues, is not.",
      },
      {
        q: "Does God answer Job's question?",
        a: "No. The speech from the whirlwind is four chapters of questions about creation and never addresses why Job suffered. What changes is Job's need for the answer rather than his access to it.",
      },
    ],
    topics: ["bible-verses-about-grief"],
  },
  {
    slug: "psalms",
    name: "Psalms",
    testament: "old",
    genre: "wisdom",
    order: 19,
    chapters: 150,
    author: "Many, over centuries; roughly half are attributed to David",
    written:
      "Composed across several centuries; collected by the post-exilic period",
    audience: "Anyone praying, in any condition",
    argument:
      "The prayer book of a nation, and about a third of it is complaint — which is why it is the part of the Bible people return to when everything else has stopped helping.",
    summary: [
      "The Psalms are five collections in one, 150 poems written across centuries by many hands, for public worship and private use. They are the most-read part of the Bible on almost every platform that measures such things.",
      "The most surprising fact about them for a new reader is the proportion of lament. Roughly a third are complaints — about enemies, illness, betrayal, silence from God — and several end unresolved, with no turn to praise at the finish. They were kept anyway, which is the strongest evidence in Scripture that unhappy prayer is still prayer.",
      "They also contain the most quoted lines in the language: the shepherd, the valley, the still small hours, the God who is a refuge. Reading them in whole psalms rather than as extracted verses changes almost every one of them.",
    ],
    startHere:
      "Psalm 23 if you want the famous one whole, Psalm 88 if you want to see how far the book will go — it is the one that ends in darkness with no resolution at all.",
    themes: ["lament", "praise", "kingship", "refuge"],
    keyChapters: [
      {
        chapter: 22,
        why: "The psalm quoted from the cross, opening on abandonment and ending in praise.",
      },
      {
        chapter: 88,
        why: "The one that ends in darkness with no turn at all, and was kept anyway.",
      },
      {
        chapter: 119,
        why: "The longest chapter in the Bible, an acrostic of 176 verses about one subject.",
      },
    ],
    known: {
      text: "Yahweh is my shepherd; I shall lack nothing.",
      ref: "Psalm 23:1",
    },
    misread:
      "That the Psalms are mostly praise. Lament is the largest single category, and the book's willingness to keep unresolved complaints is what makes it usable on a bad night.",
    faq: [
      {
        q: "How many psalms are there?",
        a: "150, arranged in five books. Roughly half carry David's name in their headings, though the collection spans several centuries and many authors.",
      },
      {
        q: "Which psalm should I read when I am struggling?",
        a: "Psalm 13 for a short complaint that turns, Psalm 88 for one that does not, Psalm 42 for talking to yourself when prayer is not working, and Psalm 23 when you want something you already half know.",
      },
    ],
    passages: ["psalm-23", "psalm-91", "psalm-46-10"],
    topics: ["bible-verses-for-anxiety-at-night", "bible-verses-about-peace"],
  },
  {
    slug: "proverbs",
    name: "Proverbs",
    testament: "old",
    genre: "wisdom",
    order: 20,
    chapters: 31,
    author: "Attributed to Solomon, with named collections by others",
    written: "Collected over centuries; final form likely post-exilic",
    audience: "A young person about to make a series of avoidable mistakes",
    argument:
      "A book of general truths about how life usually goes, which stops working the moment it is read as a book of promises.",
    summary: [
      "Proverbs opens with nine chapters of extended argument addressed to a son, personifying wisdom as a woman calling in the street, and then becomes what most people picture: several hundred short sayings on money, speech, work, friendship, anger and sex.",
      "The single most useful thing to know about it is the genre. A proverb is a generalisation, not a guarantee — it describes what tends to happen, and the same canon contains Job and Ecclesiastes, both of which exist partly to say that the tendency has exceptions.",
      "Read that way it is extraordinarily practical, and its observations about how a person talks are the sharpest short writing on the subject anywhere.",
    ],
    startHere:
      "Chapter 3 for the best-known passage, or chapter 10 if you want the short sayings without the long introduction.",
    themes: ["wisdom", "speech", "money", "the fear of the Lord"],
    keyChapters: [
      {
        chapter: 3,
        why: "Trust in the Lord with all your heart, and the promise about paths that is regularly misread.",
      },
      {
        chapter: 8,
        why: "Wisdom personified as a woman present at creation, which the New Testament reaches for later.",
      },
      {
        chapter: 31,
        why: "The capable woman — a poem about competence and commerce rather than about domestic virtue.",
      },
    ],
    known: {
      text: "Trust in Yahweh with all your heart, and don't lean on your own understanding.",
      ref: "Proverbs 3:5",
    },
    misread:
      "That its sayings are promises. Train up a child, commit your work, honour God and prosper — read as guarantees these produce real damage, and the book's own neighbours in the canon contradict that reading.",
    faq: [
      {
        q: "Is Proverbs a book of promises?",
        a: "No. Proverbs states general patterns — what usually happens — and Job and Ecclesiastes exist in part to insist that the pattern has exceptions. Read as guarantees, its sayings hurt people.",
      },
      {
        q: "How many chapters are in Proverbs?",
        a: "Thirty-one, which is why the common practice is a chapter a day for a month.",
      },
    ],
    passages: ["proverbs-3-5-6"],
    topics: ["bible-verses-about-self-control"],
  },
  {
    slug: "ecclesiastes",
    name: "Ecclesiastes",
    testament: "old",
    genre: "wisdom",
    order: 21,
    chapters: 12,
    author:
      '"The Preacher", traditionally Solomon; the Hebrew suggests a later hand',
    written: "Likely third century BC",
    audience: "Anyone who has done everything right and found it insufficient",
    argument:
      "The most sceptical book in the Bible, which tries wealth, work, pleasure and wisdom, finds each of them a vapour, and lands on eating your bread and doing your work anyway.",
    summary: [
      "Ecclesiastes is written by someone who has tried everything the ancient world could offer and reports back that none of it holds. The recurring word, usually translated vanity or meaningless, is closer to vapour — something real, visible and impossible to grip.",
      "It refuses every consolation on offer. The wise die like the fool. The worker leaves everything to someone who did not earn it. Nobody knows what comes after. It is a genuinely uncomfortable book and there is no evidence its editors tried to soften it.",
      "What it arrives at is small and durable: eat your bread, drink your wine, do your work with your might, enjoy the person you are with. Not because it adds up, but because the day in front of you is what you have.",
    ],
    startHere:
      "Chapter 3, which everyone half knows, and then chapter 9, which is the book at its bleakest and most bracing.",
    themes: ["vapour", "work", "death", "enjoyment"],
    keyChapters: [
      {
        chapter: 1,
        why: "The thesis stated in full: everything is vapour, and the sun rises on the same round.",
      },
      {
        chapter: 3,
        why: "A time for everything, which is colder in context than on a wall plaque.",
      },
      {
        chapter: 12,
        why: "The closing poem about ageing, which is the finest short passage on the subject anywhere.",
      },
    ],
    known: {
      text: "For everything there is a season, and a time for every purpose under heaven:",
      ref: "Ecclesiastes 3:1",
    },
    misread:
      "That chapter 3 is a comfort about timing. In context it is an argument that the seasons come regardless and are not yours to schedule, which is a considerably colder and more useful thing to be told.",
    faq: [
      {
        q: "What does vanity of vanities mean?",
        a: "The Hebrew word is hevel — vapour, breath, mist. Not that things are worthless, but that they cannot be held on to, which is a more precise and more unsettling claim.",
      },
      {
        q: "Why is Ecclesiastes in the Bible?",
        a: "Because the canon includes a book that tests every available answer and finds them insufficient. Its presence is what stops the wisdom literature from being merely optimistic.",
      },
    ],
    topics: ["bible-verses-about-time"],
  },
  {
    slug: "song-of-solomon",
    name: "Song of Solomon",
    testament: "old",
    genre: "wisdom",
    order: 22,
    chapters: 8,
    author: "Attributed to Solomon; likely a collection",
    written: "Uncertain; possibly compiled over a long period",
    audience:
      "Two lovers, and everyone since who has wondered why this is in the Bible",
    argument:
      "Eight chapters of frank love poetry with no theology in it, which the tradition has spent two thousand years allegorising and which reads perfectly well as what it is.",
    summary: [
      "The Song is a sequence of poems between two lovers, alternating voices, with a chorus of watching women. It is sensual, specific and entirely unembarrassed, and it never mentions God.",
      "For most of Christian history it was read allegorically — Christ and the church, God and Israel — partly because the plain reading was considered indecorous. Most modern readers, including most modern Christian readers, take it as love poetry that the canon includes because embodied love is not a lesser subject.",
      "Its final chapter contains the only line most people can quote, and it is the one moment the book raises its voice into something like a thesis: love is strong as death, and it cannot be bought.",
    ],
    startHere:
      "Chapter 8, for the passage everyone quotes, then chapter 2 if you want the poetry at its most characteristic.",
    themes: ["desire", "mutuality", "longing", "the garden"],
    keyChapters: [
      {
        chapter: 2,
        why: "The poetry at its most characteristic, and the source of several familiar images.",
      },
      {
        chapter: 5,
        why: "The night search through the city, which is the book's most unsettling passage.",
      },
      {
        chapter: 8,
        why: "Love strong as death, and the line about many waters that everybody knows.",
      },
    ],
    known: {
      text: "Many waters can't quench love, neither can floods drown it. If a man would give all the wealth of his house for love, he would be utterly scorned.",
      ref: "Song of Solomon 8:7",
    },
    misread:
      "That it must be an allegory to belong. The allegorical reading is ancient and respectable, but the book gives no internal signal that it is one, and reading it as love poetry is not a modern liberty.",
    faq: [
      {
        q: "What is the Song of Solomon about?",
        a: "Love and desire between two people, in alternating poems, with no explicit theology. Its inclusion in the canon is an argument in itself about how embodied love is regarded.",
      },
      {
        q: "Is the Song of Solomon an allegory?",
        a: "It has been read that way for most of Christian history — Christ and the church — but nothing in the text signals allegory, and the plain reading has a long history too.",
      },
    ],
  },
];
